import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * SiteOperations.io theme system (design system v2).
 *
 * Drives Tailwind's `darkMode: 'class'` strategy and the `--so-*` CSS-variable
 * overrides by toggling the `dark` class on <html>.
 *
 * - No stored preference (default): follows the OS via
 *   `prefers-color-scheme`, live — this reproduces Tailwind's old `media`
 *   behavior for existing dark-mode users (CRM).
 * - Explicit preference: persisted to localStorage('siteops.theme') and the
 *   OS preference is ignored until `setTheme('system')` clears it.
 */

type ResolvedTheme = 'light' | 'dark';
type ThemePreference = ResolvedTheme | 'system';

interface ThemeContextValue {
  /** The currently applied (resolved) theme. */
  theme: ResolvedTheme;
  /** Set an explicit theme, or 'system' to clear it and follow the OS. */
  setTheme: (t: ThemePreference) => void;
  /** Flip the current resolved theme and persist the result. */
  toggle: () => void;
}

const STORAGE_KEY = 'siteops.theme';

function readStoredPreference(): ResolvedTheme | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredPreference(value: ResolvedTheme | null): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (value === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // Storage unavailable (private mode, disabled) — theme still works in-memory.
  }
}

function getSystemTheme(): ResolvedTheme {
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  } catch {
    // Fall through to the light default.
  }
  return 'light';
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Explicit user preference ('light' | 'dark'); null = follow the OS.
  const [preference, setPreference] = useState<ResolvedTheme | null>(readStoredPreference);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  const theme: ResolvedTheme = preference !== null ? preference : systemTheme;

  // Apply/remove the `dark` class on <html> whenever the resolved theme changes.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Track OS preference changes live. An explicit preference always wins over
  // systemTheme in the resolution above, so this listener only affects the
  // resolved theme while no preference is stored — i.e. we "stop following the
  // OS" automatically once the user picks a theme, and resume when it's cleared.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14 fallback.
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  const setTheme = useCallback((t: ThemePreference) => {
    if (t === 'system') {
      setPreference(null);
      writeStoredPreference(null);
      // Re-sync in case the OS changed while we weren't following it.
      setSystemTheme(getSystemTheme());
      return;
    }
    setPreference(t);
    writeStoredPreference(t);
  }, []);

  const toggle = useCallback(() => {
    const next: ResolvedTheme = theme === 'dark' ? 'light' : 'dark';
    setPreference(next);
    writeStoredPreference(next);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggle }),
    [theme, setTheme, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
